import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  CLOCK_QUEUE_EVENT,
  enqueueClockTap,
  flushClockQueue,
  newClockRequestId,
  pendingClockTaps,
  sendClockTap,
  type ClockTapResult,
  type QueuedClockTap,
} from './clock-queue';

/** How often a screen showing pending taps tries again while they wait. */
export const CLOCK_QUEUE_RETRY_MS = 30_000;

/**
 * The signed-in hand's queued clock taps, kept current, with the flushes a
 * screen owes them: on mount, on the browser's `online` event, and every 30s
 * while anything is waiting. Every flush that moved something re-reads the
 * shift, so the screen shows what the server now holds.
 */
export function useClockQueue(userId: string | null | undefined) {
  const qc = useQueryClient();
  const [pending, setPending] = useState<QueuedClockTap[]>([]);

  const refresh = useCallback(async () => {
    setPending(await pendingClockTaps(userId));
  }, [userId]);

  const flush = useCallback(async () => {
    try {
      const sent = await flushClockQueue();
      if (sent.size > 0) qc.invalidateQueries({ queryKey: ['timesheet-status'] });
      return sent;
    } finally {
      await refresh();
    }
  }, [qc, refresh]);

  useEffect(() => {
    void flush();
    const onChange = () => { void refresh(); };
    const onOnline = () => { void flush(); };
    window.addEventListener(CLOCK_QUEUE_EVENT, onChange);
    window.addEventListener('online', onOnline);
    return () => {
      window.removeEventListener(CLOCK_QUEUE_EVENT, onChange);
      window.removeEventListener('online', onOnline);
    };
  }, [flush, refresh]);

  useEffect(() => {
    if (pending.length === 0) return;
    const t = setInterval(() => { void flush(); }, CLOCK_QUEUE_RETRY_MS);
    return () => clearInterval(t);
  }, [pending.length, flush]);

  return { pending, flush, refresh };
}

/** The note both clock surfaces show while taps wait on the device. */
export function clockQueuedText(count: number): string {
  // "when you're back online" was wrong whenever the phone WAS online and the
  // server answered 5xx — the tap stays queued then too. Say what is true in both.
  return `Not saved yet — retrying. ${count === 1 ? '1 clock tap is' : `${count} clock taps are`} held on this device with the time you tapped.`;
}

/** A refused tap, shaped like the server's error so a caller's existing error handling reads it. */
export function clockTapRefusal(r: ClockTapResult): Error {
  return Object.assign(new Error(r.error ?? 'Refused'), { response: { status: r.status, data: { error: r.error } } });
}

/**
 * ── ONE WAY TO CLOCK IN OR OUT, FOR EVERY SCREEN THAT HAS A CLOCK BUTTON ──
 *
 * ClockInScreen and the home card (FieldClockAction) both tap through this, so
 * a tap made on one is the same queued tap the other shows. Every tap is
 * written to the device queue first, then sent (lib/offline/clock-queue.ts).
 *
 * `server` is the GET /timesheets/status body the screen already reads. While
 * taps are queued, the LAST one decides what the hand sees: a queued clock-in
 * is a shift on the clock timed from the tap; a queued clock-out is a shift
 * ended. The server's answer takes over when the queue is empty.
 *
 * tapClockIn / tapClockOut resolve with the tap's outcome — 'queued' when it is
 * safe on the device and not yet at the server — and reject with the server's
 * refusal (or 'Not clocked in' when there is no shift to close).
 */
export function useClockTaps(
  userId: string | null | undefined,
  server: { status?: string; timesheet?: any } | null | undefined,
) {
  const { pending, flush } = useClockQueue(userId);
  const lastTap: QueuedClockTap | undefined = pending[pending.length - 1];
  const shiftOnlyOnDevice = lastTap?.action === 'clock_in';
  const status: string = lastTap
    ? (lastTap.action === 'clock_in' ? 'active' : 'clocked_out')
    : (server?.status ?? 'clocked_out');
  const clockInAt: string | undefined = shiftOnlyOnDevice
    ? lastTap!.device_tapped_at
    : server?.timesheet?.clock_in_at ?? server?.timesheet?.clockInAt ?? server?.timesheet?.clock_in;

  /**
   * Queue, then send. A device whose cache cannot be written sends once,
   * directly — the server is idempotent on the request id either way.
   */
  const submitTap = useCallback(async (tap: Omit<QueuedClockTap, 'queued_at' | 'attempts'>): Promise<ClockTapResult> => {
    let queued = true;
    try {
      await enqueueClockTap(tap);
    } catch {
      queued = false;
    }
    if (queued) {
      const sent = await flush();
      return sent.get(tap.client_request_id)
        ?? { client_request_id: tap.client_request_id, action: tap.action, outcome: 'queued' };
    }
    const once = await sendClockTap({ ...tap, queued_at: new Date().toISOString(), attempts: 0 });
    if (!once.answered) {
      throw new Error(`${tap.action === 'clock_in' ? 'Clock in' : 'Clock out'} was not saved — this device cannot hold it offline. Check your connection and tap again.`);
    }
    return once.result;
  }, [flush]);

  /** `tappedAt` is taken by the caller at the tap, before any GPS wait. */
  const tapClockIn = useCallback(async (body: Record<string, unknown>, tappedAt: string) => {
    const r = await submitTap({
      client_request_id: newClockRequestId(), action: 'clock_in', device_tapped_at: tappedAt,
      user_id: userId as string, body,
    });
    if (r.outcome === 'refused') throw clockTapRefusal(r);
    return r;
  }, [submitTap, userId]);

  /**
   * Closes the shift BY NAME — a late replay must never close the next one. A
   * shift that exists only on this device has no id yet; the tap waits on its
   * clock-in instead (after_request_id).
   */
  const tapClockOut = useCallback(async (body: Record<string, unknown>, tappedAt: string) => {
    const timesheetId: string | null = shiftOnlyOnDevice ? null : (server?.timesheet?.id ?? null);
    const afterRequestId: string | null = shiftOnlyOnDevice ? lastTap!.client_request_id : null;
    if (!timesheetId && !afterRequestId) throw new Error('Not clocked in');
    const r = await submitTap({
      client_request_id: newClockRequestId(), action: 'clock_out', device_tapped_at: tappedAt,
      timesheet_id: timesheetId, after_request_id: afterRequestId, user_id: userId as string, body,
    });
    if (r.outcome === 'refused') throw clockTapRefusal(r);
    return { result: r, timesheetId: timesheetId ?? r.data?.timesheet_id ?? null };
  }, [submitTap, shiftOnlyOnDevice, lastTap, server, userId]);

  return { pending, lastTap, shiftOnlyOnDevice, status, clockInAt, tapClockIn, tapClockOut, flush };
}
